import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, Minus, Plus, Truck, Tag, Percent } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function CartDrawer({ open, onClose, items, onUpdate, onRemove }) {
  const [timer, setTimer] = useState({ m: 4, s: 53 });
  const navigate = useNavigate();
  const { isAuthed } = useAuth();

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => {
      setTimer(prev => {
        let { m, s } = prev;
        if (s > 0) s--; else { s = 59; if (m > 0) m--; }
        return { m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, [open]);

  const pad = n => String(n).padStart(2, '0');

  const subtotal = items.reduce((sum, it) => sum + it.price * (it.qty || 1), 0);
  const savings = items.reduce((sum, it) => sum + (it.originalPrice - it.price) * (it.qty || 1), 0);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/50 animate-[fadeInBg_0.2s_ease-out]" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-white shadow-2xl flex flex-col animate-[slideInRight_0.3s_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 shrink-0">
          <h2 className="font-serif text-2xl text-neutral-900">
            Cart <span className="text-neutral-400">•</span> <span className="text-neutral-700 text-xl">{items.length} item{items.length !== 1 && 's'}</span>
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-neutral-100 transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-neutral-100 grid place-items-center mb-4">
              <Tag className="w-8 h-8 text-neutral-400" />
            </div>
            <h3 className="font-serif text-xl text-neutral-900">Your cart is empty</h3>
            <p className="text-sm text-neutral-600 mt-2">Add some handcrafted resin art to get started.</p>
            <button onClick={onClose} className="mt-6 bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-6 py-3 rounded-md font-semibold transition-colors">
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto">
              {/* Reservation timer */}
              <div className="bg-black text-white text-center py-3 text-sm font-medium">
                Cart reserved for {pad(timer.m)}:{pad(timer.s)}
              </div>

              {/* Offers progress */}
              <div className="px-5 pt-5 pb-3">
                <p className="text-center text-sm font-semibold text-neutral-900">
                  Congrats! You&#39;ve unlocked all offers!
                </p>
                <div className="mt-3 relative">
                  <div className="h-1.5 rounded-full bg-gradient-to-r from-[#1e3a2b] via-[#2a5040] to-[#1e3a2b] relative">
                    <div className="absolute inset-0 opacity-30 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(255,255,255,.4)_4px,rgba(255,255,255,.4)_8px)] rounded-full" />
                  </div>
                  <div className="absolute inset-x-0 -top-1 flex justify-between px-4">
                    <span className="w-3.5 h-3.5 rounded-full bg-[#1e3a2b] ring-2 ring-white" />
                    <span className="w-3.5 h-3.5 rounded-full bg-[#1e3a2b] ring-2 ring-white" />
                    <span className="w-3.5 h-3.5 rounded-full bg-[#1e3a2b] ring-2 ring-white" />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 text-center text-xs">
                  <div className="flex flex-col items-center gap-1">
                    <Truck className="w-5 h-5 text-[#1e3a2b]" />
                    <span className="font-medium text-neutral-800">Free Shipping</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <Tag className="w-5 h-5 text-[#1e3a2b]" />
                    <span className="font-medium text-neutral-800">Discount</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <Percent className="w-5 h-5 text-[#1e3a2b]" />
                    <span className="font-medium text-neutral-800">70% OFF</span>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="px-5 pt-2 pb-4 space-y-5">
                {items.map((it, i) => (
                  <div key={i} className="flex gap-3">
                    <img src={it.image} alt="" className="w-20 h-20 rounded-md object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-serif text-lg text-neutral-900 leading-tight line-clamp-2">{it.title.split('|')[0]}</h4>
                        <button
                          onClick={() => onRemove(i)}
                          className="p-1 text-neutral-400 hover:text-red-600 transition-colors shrink-0"
                          aria-label="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-sm text-neutral-600 mt-0.5">{it.size}</div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="inline-flex items-center border border-neutral-300 rounded-md">
                          <button
                            onClick={() => onUpdate(i, Math.max(1, (it.qty || 1) - 1))}
                            className="px-2 py-1 hover:bg-neutral-100 transition-colors"
                            aria-label="Decrease"
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="px-3 py-1 text-sm font-semibold min-w-[36px] text-center">{it.qty || 1}</span>
                          <button
                            onClick={() => onUpdate(i, (it.qty || 1) + 1)}
                            className="px-2 py-1 hover:bg-neutral-100 transition-colors"
                            aria-label="Increase"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-neutral-500 line-through">Rs. {it.originalPrice.toLocaleString('en-IN')}.00</div>
                          <div className="font-bold text-neutral-900">Rs. {(it.price * (it.qty || 1)).toLocaleString('en-IN')}.00</div>
                        </div>
                      </div>
                      {it.originalPrice > it.price && (
                        <div className="text-xs text-[#8b6c3d] font-semibold mt-1">
                          (You save Rs. {((it.originalPrice - it.price) * (it.qty || 1)).toLocaleString('en-IN')})
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-100 px-5 py-4 shrink-0 bg-white">
              <div className="flex items-center justify-between text-sm text-[#8b6c3d] font-medium">
                <span>Savings</span>
                <span>-Rs. {savings.toLocaleString('en-IN')}.00</span>
              </div>
              <div className="mt-1 flex items-baseline justify-between">
                <span className="font-serif text-2xl text-neutral-900">Subtotal</span>
                <span className="font-bold text-2xl text-neutral-900">Rs. {subtotal.toLocaleString('en-IN')}.00</span>
              </div>
              <button
                onClick={() => {
                  onClose();
                  navigate(isAuthed ? '/checkout' : '/login', isAuthed ? undefined : { state: { next: '/checkout' } });
                }}
                className="mt-4 w-full bg-[#1e3a2b] hover:bg-[#2a5040] text-white font-bold py-4 rounded-md tracking-wide transition-colors"
              >
                {isAuthed ? 'Checkout' : 'Login to checkout'}
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
